'use client';

import { updateOwners } from '@/_api/admin-companies';
import { useContext, useState, Fragment, useMemo, type FC } from 'react';
import { UpdateOwnersDialogProps } from './UpdateOwnersDialog.types';
import {
  Button,
  ButtonGroup,
  Dialog,
  Icon,
  Flex,
  Text,
  Heading,
} from '@/_components';
import { CompanyContext } from '@/_contexts/admin/CompanyProvider';
import { UserContext } from '@/_contexts/UserProvider';
import { ToastContext } from '@/_contexts/ToastProvider';
import styles from './UpdateOwnersDialog.module.css';
import { User } from '@/_types/models/user';
import { Company } from '@/_types/models/company';
import { OwnerAvatar } from './_components/OwnerAvatar';

export const UpdateOwnersDialog: FC<UpdateOwnersDialogProps> = ({
  isOpen,
  onClose,
  newOwners,
}) => {
  const { addToast } = useContext(ToastContext);
  const { token } = useContext(UserContext);
  const { selectedCompany, updateSelectedCompany } = useContext(CompanyContext);

  const [isLoading, setLoading] = useState<boolean>(false);

  const onSaved = () => {
    onClose();
    setLoading(false);
    return addToast(
      'success',
      'You have successfully updated the list of users.',
    );
  };

  const onMoveMessages = async () => {
    try {
      setLoading(true);
      await updateOwners({
        ownerIds: newOwnerIds,
        companyId: selectedCompany?.id!,
        token,
        copyPreviousMessages: false,
        transferPreviousMessages: true,
        sourceConversationId: selectedCompany?.relevant_conversation_id,
      });
      _updateCompany();
    } catch (error) {
      console.error(error);
    } finally {
      onSaved();
    }
  };

  const onNewConversation = async () => {
    try {
      setLoading(true);
      await updateOwners({
        ownerIds: newOwnerIds,
        companyId: selectedCompany?.id!,
        token,
        copyPreviousMessages: false,
        transferPreviousMessages: false,
        sourceConversationId: selectedCompany?.relevant_conversation_id,
      });
      _updateCompany();
    } catch (error) {
      console.error(error);
    } finally {
      onSaved();
    }
  };

  const newOwnerIds: Array<number> = useMemo(
    () => newOwners.map((owner) => owner.id),
    [newOwners],
  );

  const _updateCompany = () => {
    const newCompany: Company = {
      ...selectedCompany!,
      all_owner_ids: newOwnerIds,
      owner_ids: newOwnerIds
        .filter(
          (userId) =>
            !!selectedCompany?.owners?.find((u) => u?.id === userId)?.uid,
        )
        .sort((a, b) => a - b),
    };

    return updateSelectedCompany(newCompany);
  };

  const newUser: User | undefined = newOwners?.find(
    (newOwner) => !selectedCompany?.owners?.some((su) => su.id === newOwner.id),
  );

  const removingUserId: number | undefined = selectedCompany?.owner_ids?.find(
    (id) =>
      !newOwnerIds
        .filter(
          (userId) =>
            selectedCompany?.owners?.find((u) => u?.id === userId)?.uid,
        )
        .includes(id),
  );

  const removingUser: User | undefined = selectedCompany?.owners?.find(
    (u) => u.id === removingUserId,
  );

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title="How to manage the conversation?"
    >
      <Dialog.Content>
        <Flex isColumn={true} gap={8}>
          {!!newOwners?.length && (
            <Flex isColumn={true} gap={4}>
              <Heading level={5}>Active users in new conversation</Heading>
              <Flex gap={4} isWrapping={true}>
                {newOwners?.map((owner, key) => {
                  return (
                    <Fragment key={key}>
                      <OwnerAvatar
                        name={owner.full_name}
                        isAdding={newUser?.id === owner.id}
                      />
                    </Fragment>
                  );
                })}
                {removingUser?.id && (
                  <OwnerAvatar
                    name={removingUser?.full_name}
                    isRemoving={true}
                  />
                )}
              </Flex>
            </Flex>
          )}
          <Flex gap={8}>
            <Flex
              isColumn={true}
              gap={6}
              isCentered={true}
              className={styles.updateOwnersDialog__item}
            >
              <Icon name="chats" size={24} color="accent" />
              <Heading level={5} color="accent">
                Create new Conversation
              </Heading>
              <Text type="small" textAlign="center">
                Starts a new conversation and archive the current one. Only
                admins in the archived conversation can still view the messages.
              </Text>
              <ButtonGroup align="center">
                <Button
                  value="Start New Conversation"
                  intent="primary"
                  isDisabled={isLoading}
                  onClick={onNewConversation}
                />
              </ButtonGroup>
            </Flex>
            <Flex
              isColumn={true}
              gap={6}
              isCentered={true}
              className={styles.updateOwnersDialog__item}
            >
              <Icon name="user-plus" size={24} color="accent" />
              <Heading
                level={5}
                color="accent"
                className={styles.textAlignCenter}
              >
                {removingUserId
                  ? 'Remove User from Conversation'
                  : 'Add new User to Conversation'}
              </Heading>
              <Text type="small" textAlign="center">
                {removingUserId
                  ? 'Remove the user from the conversation. They will not be able to access the conversation, nor send or receive messages.'
                  : 'Adds the new user to the existing conversation, giving access to all previous messages.'}
              </Text>
              <ButtonGroup align="center">
                <Button
                  value="Move Messages"
                  intent="primary"
                  isDisabled={isLoading}
                  onClick={onMoveMessages}
                />
              </ButtonGroup>
            </Flex>
          </Flex>
        </Flex>
      </Dialog.Content>
    </Dialog>
  );
};
